import { Lock, Warning } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import clsx from "clsx";
import { useState } from "react";

import LinkedInInfo from "common/components/LinkedInLogin/LinkedInInfo";
import PasswordInput from "common/components/PasswordInput";
import { ProxySelectFormField } from "common/components/Proxy";
import RenderIf from "common/components/RenderIf";
import { Button } from "common/components/ui/Button";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useWorkspaceAccountMutations from "common/datahooks/useWorkspaceAccountMutations";
import { HTTPError } from "common/helpers/HTTP";
import useDebounce from "common/hooks/useDebounce";
import useGTM from "common/hooks/useGTM";
import type { LoginSchemaType } from "common/schemas";
import type { WorkspaceAccount } from "common/types";

import type { UseFormReturn } from "react-hook-form";

interface LoginFormProps {
  formReturn: UseFormReturn<LoginSchemaType>;
  account: WorkspaceAccount | undefined;
  onSuccess: () => void;
  setStep: (step: "thiswasme" | "pin") => void;
  setCurrentAction: (currentAction: "selectMethod" | "addProxy") => void;
  selectedProxy: string;
  setSelectedProxy: (newProxy: string) => void;
}

export default function LoginForm({
  account,
  formReturn: {
    setError,
    register,
    formState: { errors },
    handleSubmit,
  },
  onSuccess,
  setStep,
  setCurrentAction,
  selectedProxy,
  setSelectedProxy,
}: LoginFormProps) {
  const { sendEvent } = useGTM();
  const { login, isLoggingIn } = useWorkspaceAccountMutations();
  const [shouldShowDelayMessage] = useDebounce(isLoggingIn, 7000);

  const [selectedProxyError, setSelectedProxyError] = useState("");

  function onSubmit(loginData: LoginSchemaType) {
    if (!selectedProxy) {
      setSelectedProxyError("Proxy is required");
      return;
    }
    setSelectedProxyError("");
    login({ loginData: { ...loginData, selectedProxy } })
      .then((response) => {
        if (response.loginResult === "PASS") {
          // Log to Sentry if full_name is missing
          if (!response.profile.full_name) {
            Sentry.setExtra("response_json", JSON.stringify(response));
            Sentry.captureMessage("Login - missing full_name");
          }

          onSuccess();
        } else if (response.loginResult === "CHALLENGE") {
          Sentry.setExtra("response_json", JSON.stringify(response));

          const { challengeType } = response;
          if (
            [
              "EmailPinChallenge",
              "SMSPinChallenge",
              "AuthenticatorAppChallenge",
            ].includes(challengeType)
          ) {
            Sentry.captureMessage("Login challenge");
            setStep("pin");
          } else if (challengeType === "CaptchaChallenge") {
            Sentry.captureMessage("Login - Captcha challenge");
            setError("password", {
              message: "Captcha challenge",
            });
          } else if (challengeType === "AppChallenge") {
            setStep("thiswasme");
          } else {
            Sentry.captureMessage("Login - Unknown challenge");
          }
        } else if (response.loginResult === "BAD_PASSWORD") {
          setError("password", {
            message: "Wrong password",
          });
        }
      })
      .catch((err) => {
        Sentry.setExtra("error", err);
        Sentry.setExtra("error_json", JSON.stringify(err));
        Sentry.captureMessage(
          `Login error${err instanceof HTTPError ? ` - ${err.type}` : ""}`,
        );

        sendEvent("connect_linkedin_account_fail");

        let errorMessage: string;
        if (
          err instanceof HTTPError &&
          err.serverMessage ===
            "logging into this account is forbidden by linkedin"
        ) {
          errorMessage = "Logging into this account is forbidden by LinkedIn";
        } else if (err.code === 409) {
          // this happens when the account is already connected to another workspace
          errorMessage = "Account already connected to another workspace";
        } else if (err.code === 401) {
          errorMessage = "Invalid email or password";
        } else {
          errorMessage = "Error connecting account";
        }

        setError("password", {
          message: errorMessage,
        });
      });
  }
  return (
    <form className="flex flex-col" onSubmit={handleSubmit(onSubmit)}>
      {account && (
        <LinkedInInfo
          pictureUrl={account.picture_url}
          fullName={account.full_name}
          occupation={account.occupation}
        />
      )}

      <RenderIf condition={!account?.email}>
        <FormField
          label="Email"
          htmlFor="email"
          error={errors.email?.message}
          className={account && "mt-8 lg:mt-4"}
        >
          <Input id="email" placeholder="Enter Email" {...register("email")} />
        </FormField>
      </RenderIf>

      <FormField
        label="Password"
        htmlFor="password"
        error={errors.password?.message}
        className={clsx(
          "lg:mt-4 mb-4 border-b border-b-black-300 pb-4",
          account?.email ? "mt-8" : "mt-4",
        )}
      >
        <PasswordInput
          id="password"
          {...register("password")}
          placeholder="Enter password"
        />
      </FormField>

      <ProxySelectFormField
        selectedProxy={selectedProxy}
        setSelectedProxy={setSelectedProxy}
        onAddProxy={() => setCurrentAction("addProxy")}
        error={selectedProxyError}
      />

      {account ? (
        <>
          <Button
            className="mb-3 mt-8 lg:mt-12"
            type="submit"
            isLoading={isLoggingIn}
          >
            Connect Account
          </Button>

          <div className="flex items-center justify-center gap-x-0.5">
            <Lock weight="fill" className="size-4 text-black-700" />

            <span className="text-xs text-black-700">
              Secured with TLS 1.3 encryption
            </span>
          </div>
        </>
      ) : (
        <div className="mt-8 flex gap-x-2 lg:gap-x-4">
          <Button
            variant="secondary-black"
            className="flex-1"
            disabled={isLoggingIn}
            onClick={() => setCurrentAction("selectMethod")}
          >
            Back
          </Button>
          <Button className="flex-1" type="submit" isLoading={isLoggingIn}>
            Connect
          </Button>
        </div>
      )}
      {/* Long Loading text */}
      <RenderIf condition={isLoggingIn && shouldShowDelayMessage}>
        <div className="ml-1 flex items-center text-yellow-500 gap-0.5 mt-4">
          <Warning weight="fill" size={16} />
          <p className="text-xs">
            The login process is taking some time, please do not close or
            refresh this page
          </p>
        </div>
      </RenderIf>
    </form>
  );
}
